// Third party
import React from 'react';
import { connect } from 'react-redux';
import Calendar, { CalendarType, Detail, FormatterCallback } from 'react-calendar';
import classNames from 'classnames';
import moment from 'moment';
import Input from './Input';
import Nullable from '@/types/nullable';
import Locale from '@/types/locale';
import { formatMessage } from '@/utils/i18n';

export type DatePickerDetail = 'month' | 'year' | 'decade' | 'century';

type Props = {
  className?: string;
  datePickerClassName?: string;
  minDate?: Nullable<Date>;
  maxDate?: Nullable<Date>;
  dark?: boolean;
  locale?: Locale;
  onDateChange: (date: Date) => void;
  calendarType?: CalendarType;
  icon?: string;
  formatShortWeekday?: FormatterCallback;
  navigationLabel?: (props: { date: Date; view: Detail; label: string }) => string | JSX.Element | null;
} & Partial<DefaultProps>;

type DefaultProps = {
  defaultDate?: Nullable<Date>;
  minDetail?: DatePickerDetail;
  maxDetail?: DatePickerDetail;
  compact?: boolean;
  minDate?: Nullable<Date>;
  maxDate?: Nullable<Date>;
  dark?: boolean;
  allowPrevNext2?: boolean;
  customFormat?: string;
  disabled?: boolean;
  invert?: boolean;
  customPlaceholder?: string;
};

interface State {
  isOpen: boolean;
  date?: Nullable<Date>;
}

class DatePicker extends React.Component<Props, State> {
  private container = React.createRef<HTMLDivElement>();

  static defaultProps: DefaultProps = {
    defaultDate: null,
    minDetail: 'month',
    maxDetail: 'month',
    compact: false,
  };

  readonly state: State = {
    isOpen: false,
    date: this.props.defaultDate,
  };

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
    document.addEventListener('keydown', this.handleEscapeKey);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
    document.removeEventListener('keydown', this.handleEscapeKey);
  }

  componentWillReceiveProps(nextProps: Props) {
    if (this.props.defaultDate !== nextProps.defaultDate) {
      this.setState({ date: nextProps.defaultDate });
    }
  }

  handleClickOutside = (event: Event) => {
    if (!this.state.isOpen) {
      return;
    }

    if (!(event.target instanceof Node) || !this.container.current) {
      return;
    }

    if (!this.container.current.contains(event.target)) {
      this.setState({ isOpen: false });
    }
  };

  handleEscapeKey = (event: KeyboardEvent) => {
    if (this.state.isOpen && event.key === 'Escape') {
      event.preventDefault();
      this.setState({ isOpen: false });
    }
  };

  onChange = (value: Date | Date[]) => {
    const date: Date = value instanceof Array ? value[0] : value;
    this.setState({ isOpen: false, date });
    this.props.onDateChange(date);
  };

  handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === 'ArrowDown') {
      event.preventDefault();
      this.setState({ isOpen: true });
    }
  };

  getDateFormat = () => {
    const { customFormat, maxDetail } = this.props;

    if (customFormat) {
      return customFormat;
    }

    switch (maxDetail) {
      case 'year':
        return 'YYYY-MM';
      default:
        return 'YYYY-MM-DD';
    }
  };

  render() {
    const { className, datePickerClassName, compact, customPlaceholder, dark, minDate, maxDate, allowPrevNext2, disabled, locale, icon, invert, ...props } =
      this.props;
    const formattedDate = this.state.date ? moment.utc(this.state.date).format(this.getDateFormat()) : '';

    return (
      <div className={classNames('date-picker', className)} ref={this.container}>
        <Input
          leftSection={icon ? <i className={icon} /> : undefined}
          compact={compact}
          dark={dark}
          className="datepicker-input-field no-print"
          onClick={() => this.setState({ isOpen: true })}
          onKeyDown={this.handleKeyDown}
          placeholder={customPlaceholder || this.getDateFormat()}
          value={formattedDate}
          disabled={disabled}
          invert={invert}
        />

        <span className="print-only">{formattedDate}</span>

        {this.state.isOpen && (
          <Calendar
            {...props}
            className={classNames('datepicker-calendar', datePickerClassName)}
            prev2Label={allowPrevNext2 ? '«' : null}
            next2Label={allowPrevNext2 ? '»' : null}
            prevLabel={
              <span
                aria-label={`${formatMessage('Common.Accessibility.GoToPreviousYear')} (${
                  this.state.date ? moment(this.state.date).subtract(1, 'year').format('YYYY') : ''
                })`}
              >
                ‹
              </span>
            }
            nextLabel={
              <span
                aria-label={`${formatMessage('Common.Accessibility.GoToNextYear')} (${
                  this.state.date ? moment(this.state.date).add(1, 'year').format('YYYY') : ''
                })`}
              >
                ›
              </span>
            }
            value={this.state.date || undefined}
            locale={locale}
            onChange={this.onChange}
            minDate={minDate || undefined}
            maxDate={maxDate || undefined}
          />
        )}
      </div>
    );
  }
}

export default connect()(DatePicker);
